import dayjs from "dayjs";

import { CalendarIcon } from "@assets";
import { ProgressBar } from "@components/ProgressBar";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Body, BodySmall } from "@components/Typography";
import { GoalType } from "@ctypes";
import { useGoalsContext, useTheme } from "@hooks";
import { i18n, Translations } from "@i18n";
import { convertCentsToDollar, formatCurrency } from "@utils";
import { useStyles } from "./styles";

const getTimeDisplay = (totalDays: number) => {
  if (totalDays <= 0) {
    return i18n.t(Translations.GoalBreakdownRouteTrumpSidecarConversionWeeks, {
      weeks: 0,
    });
  }

  if (totalDays > 365) {
    const years = Math.floor(totalDays / 365);
    return i18n.t(Translations.GoalBreakdownRouteTrumpSidecarConversionYears, {
      count: years,
    });
  }

  const weeks = Math.floor(totalDays / 7);
  const days = totalDays % 7;

  if (days > 0) {
    return i18n.t(Translations.GoalBreakdownRouteTrumpSidecarConversionWeeksDays, { weeks, days });
  }

  return i18n.t(Translations.GoalBreakdownRouteTrumpSidecarConversionWeeks, {
    weeks,
  });
};

export const TrumpSidecarDetails = () => {
  const { theme } = useTheme();
  const styles = useStyles();
  const { goal } = useGoalsContext();

  if (goal?.type !== GoalType.TrumpAccountSidecar) return null;
  if (!goal.endDate) return null;

  const now = dayjs();
  const end = dayjs(goal.endDate);
  const totalDays = end.diff(now, "day");
  const timeDisplay = getTimeDisplay(totalDays);
  const formattedDate = end.format("MMMM D, YYYY");

  const start = dayjs(goal.startDate);
  const totalDuration = end.diff(start, "day");
  const elapsed = now.diff(start, "day");
  const progress = totalDuration > 0 ? Math.min(Math.max(elapsed / totalDuration, 0), 1) : 0;

  const reserveAmount = formatCurrency(convertCentsToDollar(goal.currentAmountInCents ?? 0), true);

  const irsBillAmount = formatCurrency(convertCentsToDollar(goal.estimatedIrsBillInCents ?? 0));

  return (
    <div style={styles.wrapper}>
      <div style={styles.conversionCard}>
        <div style={styles.conversionHeader}>
          <div style={styles.conversionContent}>
            <BodySmall style={styles.label}>
              {i18n.t(Translations.GoalBreakdownRouteTrumpSidecarConversionLabel)}
            </BodySmall>
            <Spacing size={SpacingSizes.XS} />
            <Body style={styles.value}>{timeDisplay}</Body>
            <Spacing size={SpacingSizes.XXS} />
            <BodySmall style={styles.dateText}>{formattedDate}</BodySmall>
          </div>
          <CalendarIcon fill={theme.exclusive.default} size={32} />
        </div>
        <Spacing size={SpacingSizes.S} />
        <ProgressBar
          progress={progress}
          barHeight={4}
          color={theme.exclusive.default}
          backgroundColor={theme.goal.progress.bar.background}
        />
      </div>

      <div style={styles.bottomRow}>
        <div style={styles.halfCard}>
          <BodySmall style={styles.label}>
            {i18n.t(Translations.GoalBreakdownRouteTrumpSidecarReserveLabel)}
          </BodySmall>
          <Spacing size={SpacingSizes.XXXS} />
          <Body style={styles.halfCardValue}>{reserveAmount}</Body>
          <BodySmall style={styles.subtitle}>
            {i18n.t(Translations.GoalBreakdownRouteTrumpSidecarReserveSubtitle)}
          </BodySmall>
        </div>
        <div style={styles.halfCard}>
          <BodySmall style={styles.label}>
            {i18n.t(Translations.GoalBreakdownRouteTrumpSidecarIrsBillLabel)}
          </BodySmall>
          <Spacing size={SpacingSizes.XXXS} />
          <Body style={styles.halfCardValue}>{irsBillAmount}</Body>
          <BodySmall style={styles.subtitle}>
            {i18n.t(Translations.GoalBreakdownRouteTrumpSidecarIrsBillSubtitle)}
          </BodySmall>
        </div>
      </div>
      <Spacing size={SpacingSizes.M} />
    </div>
  );
};
